import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const NeoSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Form states
  const [creditPackages, setCreditPackages] = useState([]);
  const [creditCosts, setCreditCosts] = useState({
    simple_query: 1,
    complex_action: 5,
    analytics: 3,
    report_generation: 10
  });
  const [queryLimits, setQueryLimits] = useState({
    max_clock_records: 1000,
    max_users: 500,
    max_expenses: 100,
    max_payroll_records: 500,
    max_tasks: 100
  });
  const [initialCredits, setInitialCredits] = useState({
    admin: 50,
    team: 10
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch Neo settings
        const neoResponse = await axios.get(`${API}/super-admin/neo/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const data = neoResponse.data.settings;
        
        setSettings(data);
        setCreditPackages(data.credit_packages || []);
        setCreditCosts(data.credit_costs || {});
        setQueryLimits(data.query_limits || {
          max_clock_records: 1000,
          max_users: 500,
          max_expenses: 100,
          max_payroll_records: 500,
          max_tasks: 100
        });
        setInitialCredits({
          admin: data.initial_credits_admin || 50,
          team: data.initial_credits_team || 10
        });
        
        // Fetch subscription packages separately to handle errors independently
        try {
          const packagesResponse = await axios.get(`${API}/super-admin/subscription-packages`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const packages = packagesResponse.data || [];
          setSubscriptionPackages(packages);
        } catch (pkgError) {
          console.error('Failed to fetch subscription packages:', pkgError);
          // Don't fail the whole page if packages fail to load
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch Neo settings:', error);
        setErrorMessage('Failed to load settings');
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleTriggerMonthlyAllocation = async () => {
    try {
      setAllocating(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      await axios.post(`${API}/super-admin/neo/trigger-monthly-allocation`);
      
      setSuccessMessage('Monthly credit allocation triggered successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
      setAllocating(false);
    } catch (error) {
      console.error('Failed to trigger monthly allocation:', error);
      setErrorMessage('Failed to trigger monthly allocation');
      setAllocating(false);
    }
  };

  const handleSavePackages = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      await axios.put(`${API}/super-admin/neo/settings/packages`, creditPackages);
      
      setSuccessMessage('Credit packages updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setSaving(false);
    } catch (error) {
      console.error('Failed to save packages:', error);
      setErrorMessage('Failed to save credit packages');
      setSaving(false);
    }
  };

  const handleSaveCosts = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      await axios.put(`${API}/super-admin/neo/settings/costs`, creditCosts);
      
      setSuccessMessage('Credit costs updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setSaving(false);
    } catch (error) {
      console.error('Failed to save costs:', error);
      setErrorMessage('Failed to save credit costs');
      setSaving(false);
    }
  };

  const handleSaveQueryLimits = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      await axios.put(`${API}/super-admin/neo/settings/query-limits`, queryLimits);
      
      setSuccessMessage('Query limits updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setSaving(false);
    } catch (error) {
      console.error('Failed to save query limits:', error);
      setErrorMessage('Failed to save query limits');
      setSaving(false);
    }
  };

  const handleSaveInitialCredits = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      await axios.put(`${API}/super-admin/neo/settings/allocation`, {
        initial_credits_admin: initialCredits.admin,
        initial_credits_team: initialCredits.team
      });
      
      setSuccessMessage('Initial credit allocation updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setSaving(false);
    } catch (error) {
      console.error('Failed to save initial credits:', error);
      setErrorMessage('Failed to save initial credit allocation');
      setSaving(false);
    }
  };

  const handleAddPackage = () => {
    const newPackage = {
      id: `package_${Date.now()}`,
      credits: 1000,
      price_gbp: 10.00,
      label: 'New Package',
      popular: false
    };
    setCreditPackages([...creditPackages, newPackage]);
  };

  const handleUpdatePackage = (index, field, value) => {
    const updated = [...creditPackages];
    updated[index][field] = value;
    setCreditPackages(updated);
  };

  const handleDeletePackage = (index) => {
    const updated = creditPackages.filter((_, i) => i !== index);
    setCreditPackages(updated);
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading Neo settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header - Matching Subscription Packages Theme */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white mb-8 shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">Neo AI Settings</h1>
            <p className="text-purple-200 text-sm">Configure credit packages, costs, and access control for the Neo AI assistant</p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border-2 border-green-300 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-sm text-green-900 mb-1">Success!</p>
              <p className="text-green-800 text-sm">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-sm text-red-900 mb-1">Error</p>
              <p className="text-red-800 text-sm">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
          {/* Credit Packages */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">💳 Credit Packages</h2>
                    <p className="text-sm text-gray-600 mt-0.5">Configure available credit packages for purchase</p>
                  </div>
                </div>
                <button
                  onClick={handleAddPackage}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors shadow-lg hover:shadow-xl text-sm font-semibold"
                >
                  + Add Package
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-3">
                {creditPackages.map((pkg, index) => (
                  <div key={pkg.id} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Label</label>
                        <input
                          type="text"
                          value={pkg.label}
                          onChange={(e) => handleUpdatePackage(index, 'label', e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Credits</label>
                        <input
                          type="number"
                          value={pkg.credits}
                          onChange={(e) => handleUpdatePackage(index, 'credits', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Price (£)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pkg.price_gbp}
                          onChange={(e) => handleUpdatePackage(index, 'price_gbp', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors"
                        />
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center space-x-2 mt-6 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pkg.popular}
                            onChange={(e) => handleUpdatePackage(index, 'popular', e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Popular</span>
                        </label>
                      </div>
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => handleDeletePackage(index)}
                          className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-semibold border-2 border-transparent hover:border-red-200 mt-6"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSavePackages}
                  disabled={saving}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Packages'}
                </button>
              </div>
            </div>
          </div>

          {/* Monthly Credit Allocation */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">📅 Monthly Credit Allocation</h2>
                  <p className="text-sm text-gray-600 mt-0.5">Credits are automatically allocated on the 1st of each month based on subscription tiers</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-800">How it works:</span> Users receive monthly Neo credits based on their role. Admins receive <code className="bg-gray-200 px-1 rounded">neoCreditsMonthly</code> and team members receive <code className="bg-gray-200 px-1 rounded">neoCreditsMonthlyTeam</code> from their subscription package. Credits accumulate and do not expire.
                </p>
                <div className="mt-3 text-sm">
                  <p className="font-medium text-gray-700 mb-2">Monthly allocations by subscription tier:</p>
                  {subscriptionPackages.length > 0 ? (
                    <div className="space-y-2">
                      {subscriptionPackages.map((pkg, index) => {
                        // Neo credits are nested inside features
                        const features = pkg.features || {};
                        const adminCredits = parseInt(features.neoCreditsMonthly || 0);
                        const teamCredits = parseInt(features.neoCreditsMonthlyTeam || 0);
                        const hasNeoCredits = adminCredits > 0 || teamCredits > 0;
                        
                        // Determine color scheme based on package tier
                        const tierColors = {
                          free: { admin: 'bg-gray-100 text-gray-700', team: 'bg-gray-50 text-gray-600' },
                          starter: { admin: 'bg-blue-100 text-blue-800', team: 'bg-blue-50 text-blue-600' },
                          business: { admin: 'bg-purple-100 text-purple-800', team: 'bg-purple-50 text-purple-600' },
                          professional: { admin: 'bg-indigo-100 text-indigo-800', team: 'bg-indigo-50 text-indigo-600' },
                          pro: { admin: 'bg-amber-100 text-amber-800', team: 'bg-amber-50 text-amber-600' }
                        };
                        
                        const tierKey = (pkg.tier_key || pkg.name || '').toLowerCase();
                        const colors = tierColors[tierKey] || tierColors.starter;
                        
                        return (
                          <div key={pkg.id || index} className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-700 min-w-[120px]">{pkg.name}:</span>
                            {hasNeoCredits ? (
                              <>
                                <span className={`px-2 py-1 ${colors.admin} rounded font-medium text-xs`}>
                                  Admin: {adminCredits}
                                </span>
                                <span className={`px-2 py-1 ${colors.team} rounded font-medium text-xs`}>
                                  Team: {teamCredits}
                                </span>
                              </>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded font-medium text-xs italic">
                                Not configured
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No subscription packages found</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Use this button to manually trigger allocation (e.g., for testing or if the scheduled job was missed)
                </p>
                <button
                  onClick={handleTriggerMonthlyAllocation}
                  disabled={allocating}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {allocating ? 'Allocating...' : 'Trigger Monthly Allocation'}
                </button>
              </div>
            </div>
          </div>

          {/* Initial Credit Allocation */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-100 to-yellow-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">🎁 Initial Credit Allocation</h2>
                  <p className="text-sm text-gray-600 mt-0.5">Credits given to new users upon account activation (for trial exploration)</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded mb-6">
                <p className="text-amber-900 text-sm font-medium mb-2">ℹ️ About Initial Credits</p>
                <p className="text-amber-800 text-sm leading-relaxed">
                  These credits are allocated once when a user activates their account (sets up their password for the first time). 
                  This allows new users to try Neo before their organisation subscribes. Users can purchase more credits if needed.
                  Monthly allocations (based on subscription tier) will begin after the organisation has an active PayPal subscription.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Users</label>
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={initialCredits.admin}
                    onChange={(e) => setInitialCredits({...initialCredits, admin: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent hover:border-amber-400 transition-colors font-medium text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Credits allocated to admin users on account activation</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Team Members</label>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    value={initialCredits.team}
                    onChange={(e) => setInitialCredits({...initialCredits, team: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent hover:border-amber-400 transition-colors font-medium text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Credits allocated to team members on account activation</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveInitialCredits}
                  disabled={saving}
                  className="px-6 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-lg hover:from-amber-700 hover:to-yellow-700 transition-all shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Initial Allocation'}
                </button>
              </div>
            </div>
          </div>

          {/* Credit Costs */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">💰 Credit Costs per Query Type</h2>
                  <p className="text-sm text-gray-600 mt-0.5">Cost in credits for different types of Neo queries</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Simple Query (data retrieval)</label>
                  <input
                    type="number"
                    value={creditCosts.simple_query}
                    onChange={(e) => setCreditCosts({...creditCosts, simple_query: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Complex Action (approvals, assignments)</label>
                  <input
                    type="number"
                    value={creditCosts.complex_action}
                    onChange={(e) => setCreditCosts({...creditCosts, complex_action: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Analytics & Insights</label>
                  <input
                    type="number"
                    value={creditCosts.analytics}
                    onChange={(e) => setCreditCosts({...creditCosts, analytics: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Report Generation</label>
                  <input
                    type="number"
                    value={creditCosts.report_generation}
                    onChange={(e) => setCreditCosts({...creditCosts, report_generation: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveCosts}
                  disabled={saving}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Costs'}
                </button>
              </div>
            </div>
          </div>

          {/* Query Limits */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">⚙️ Query Limits</h2>
                  <p className="text-sm text-gray-600 mt-0.5">Configure maximum records returned by Neo data queries</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {/* Info Note */}
              <div className="mb-6 bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                <p className="text-purple-900 text-sm font-medium mb-2">ℹ️ About Query Limits</p>
                <p className="text-purple-800 text-sm leading-relaxed">
                  These limits control the maximum number of records Neo can retrieve in a single query. 
                  This prevents performance issues and excessive data transfer for organisations with large datasets.
                  When results exceed these limits, users will see a warning that data may be incomplete.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Max Clock Records</label>
                  <input
                    type="number"
                    min="100"
                    max="10000"
                    value={queryLimits.max_clock_records || 1000}
                    onChange={(e) => setQueryLimits({...queryLimits, max_clock_records: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Attendance records per query</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Max Users</label>
                  <input
                    type="number"
                    min="50"
                    max="5000"
                    value={queryLimits.max_users || 500}
                    onChange={(e) => setQueryLimits({...queryLimits, max_users: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Users returned in attendance queries</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Max Expenses</label>
                  <input
                    type="number"
                    min="50"
                    max="1000"
                    value={queryLimits.max_expenses || 100}
                    onChange={(e) => setQueryLimits({...queryLimits, max_expenses: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Expenses per approval/query</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Max Payroll Records</label>
                  <input
                    type="number"
                    min="100"
                    max="5000"
                    value={queryLimits.max_payroll_records || 500}
                    onChange={(e) => setQueryLimits({...queryLimits, max_payroll_records: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Records per payroll summary</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Max Tasks</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={queryLimits.max_tasks || 100}
                    onChange={(e) => setQueryLimits({...queryLimits, max_tasks: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors font-medium text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Tasks per summary query</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveQueryLimits}
                  disabled={saving}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Limits'}
                </button>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default NeoSettingsPage;
